import {
  faInfoCircle,
  faExclamationTriangle,
  faExclamationCircle,
  faCheckCircle,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import styled from "styled-components";
import { IComponent } from "../Component";

export const ALERT_COMPONENT = "alert";

export type AlertVariant = "info" | "warning" | "danger" | "success";

export interface AlertSpec {
  type: typeof ALERT_COMPONENT;
  variant: AlertVariant;
  title?: string;
  content: string;
  showTitle: boolean;
  showIcon: boolean;
}

export const getVariantColor = (variant: AlertVariant): string => {
  switch (variant) {
    case "info":
      return "#0dcaf0";
    case "warning":
      return "#ffc107";
    case "danger":
      return "#dc3545";
    case "success":
      return "#198754";
    default:
      return "#0dcaf0";
  }
};

export const getVariantIcon = (variant: AlertVariant): IconDefinition => {
  switch (variant) {
    case "info":
      return faInfoCircle;
    case "warning":
      return faExclamationTriangle;
    case "danger":
      return faExclamationCircle;
    case "success":
      return faCheckCircle;
    default:
      return faInfoCircle;
  }
};

export const AlertContainer = styled.div<{
  variant: AlertVariant;
}>`
  display: flex;
  align-items: start;
  padding: 1rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
  background-color: ${props => {
    const baseColor = getVariantColor(props.variant);
    return `${baseColor}20`; // 20 is hex for 12% opacity
  }};
  border-left: 4px solid ${props => getVariantColor(props.variant)};
`;

export const IconWrapper = styled.div`
  margin-right: 0.75rem;
  margin-top: 0.25rem;
  color: ${props => props.color};
`;

export const Content = styled.div`
  flex: 1;
`;

export const Title = styled.div`
  font-weight: bold;
  margin-bottom: 0.25rem;
`;

const Component = (props: { spec: AlertSpec }) => {
  const { variant, title, content, showTitle, showIcon } = props.spec;
  const iconColor = getVariantColor(variant);
  const icon = getVariantIcon(variant);

  return (
    <AlertContainer variant={variant}>
      {showIcon && (
        <IconWrapper color={iconColor}>
          <FontAwesomeIcon icon={icon} size="lg" />
        </IconWrapper>
      )}
      <Content>
        {showTitle && title && <Title>{title}</Title>}
        <div>{content}</div>
      </Content>
    </AlertContainer>
  );
};

export const AlertComponent: IComponent<AlertSpec> = {
  component: Component,
};
